import * as EnvironmentLayer from './add-to-cart-button/eligibility-check-strategy/environment-layer'
import * as OrderLayer from './add-to-cart-button/eligibility-check-strategy/order-layer'
import * as ProductLayer from './add-to-cart-button/eligibility-check-strategy/product-layer'

/**
 * 購入履歴でカート追加ボタンを表示するかどうかは3つのレイヤーで判断される。
 * - 環境 (environment): ホスト単位の設定や、特定のUser-Agentでは非表示にするなど、グローバルに決まるもの
 * - 注文単位 (order): 特定の注文ステータスでは非表示にするなど、注文というコンテキストが確定してから決まるもの
 * - 商品単位 (product): 特定の商品種別でのみ表示するなど、商品というコンテキストが確定してから決まるもの
 *
 * これらは上から下に向けて支配的であり、上位レイヤーで非表示と判定されれば下位レイヤーのチェックは不要である。
 * 不要なチェックの回数を減らすために、この上下関係を以下のように実装している。
 * - 環境レイヤーのチェックはbuilder生成時に完了させ、builder自身にisEligibleByEnvironmentを保持させる
 * - 注文レイヤーのチェックはbuildForOrderメソッド時に実行し、注文レイヤーで非表示と確定した場合は常にfalseを返すcheckerを返却する
 * - 商品レイヤーのチェックはbuildForOrderメソッドが返却したchecker関数で行う
 */
export class AddToCartButtonEligibilityCheckerBuilder {
  static create(env: EnvironmentLayer.ContextForEnvironmentLayer): AddToCartButtonEligibilityCheckerBuilder {
    return new AddToCartButtonEligibilityCheckerBuilder(
      defaultEligibilityCheckPolicy,
      env
    )
  }

  private readonly isEligibleByEnvironment: boolean

  private constructor(
    private readonly policy: EligibilityCheckPolicy,
    env: EnvironmentLayer.ContextForEnvironmentLayer
  ) {
    // 環境レイヤーでのチェックは1回のみ実行され、結果はbuilderが保持する
    this.isEligibleByEnvironment = this.policy.environment.check(env)
  }

  buildForOrder(order: OrderLayer.ContextForOrderLayer): AddToCartButtonEligibilityChecker {
    const alwaysIneligibleChecker = () => false

    // 環境レイヤーで非表示が確定した場合、下位レイヤーのチェックは不要
    if (!this.isEligibleByEnvironment) {
      return alwaysIneligibleChecker
    }

    // 注文レイヤーで非表示が確定した場合、商品レイヤーのチェックは不要
    if (!this.policy.order.check(order)) {
      return alwaysIneligibleChecker
    }

    return (product: ProductLayer.ContextForProductLayer) =>
      this.policy.product.check(product)
  }
}

export type AddToCartButtonEligibilityChecker = (product: ProductLayer.ContextForProductLayer) => boolean

interface EligibilityCheckPolicy {
  environment: EnvironmentLayer.EligibilityCheckerForEnvironmentLayer
  order: OrderLayer.EligibilityCheckerForOrderLayer
  product: ProductLayer.EligibilityCheckerForProductLayer
}

/**
 * ファーストリリースではホテラバをターゲットにしているが、同じ機能を他の顧客にも提供する場合は、
 * strategyを組み合わせて異なるpolicyを作成することで実現できるようにしている。
 */
const defaultEligibilityCheckPolicy: EligibilityCheckPolicy = {
  environment: new EnvironmentLayer.EligibilityCheckerForEnvironmentLayer([
    new EnvironmentLayer.EligibleWhenBehaviorIsEnabled(),
    new EnvironmentLayer.EligibleWhenNotViewedInHlAppWebView(),
  ]),
  order: new OrderLayer.EligibilityCheckerForOrderLayer([
    new OrderLayer.EligibleWhenOrderStatusIsNotPaymentRequested(),
  ]),
  product: new ProductLayer.EligibilityCheckerForProductLayer([
    new ProductLayer.EligibleWhenProductIsUserSelectable(),
    new ProductLayer.EligibleWhenProductIsContactLens(),
  ]),
}
